/**
 * Settings page handler hooks — data fetching, subscription, referral, and account actions.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const useSettingsData = (token) => {
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [referralStats, setReferralStats] = useState(null);

  useEffect(() => {
    const fetch2FAStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/2fa-status`, authHeaders(token));
        setTwoFaEnabled(response.data.two_fa_enabled);
      } catch (error) {
        logError('2FA status fetch skipped:', error?.response?.status);
      }
    };
    const fetchReferralStats = async () => {
      try {
        const response = await axios.get(`${API}/referral/stats`, authHeaders(token));
        setReferralStats(response.data);
      } catch (error) {
        logError('Referral stats fetch skipped:', error?.response?.status);
      }
    };
    fetch2FAStatus();
    fetchReferralStats();
  }, [token]);

  return { twoFaEnabled, setTwoFaEnabled, referralStats };
};

export const useSubscriptionActions = (token, user) => {
  const [openingPortal, setOpeningPortal] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [undoingCancel, setUndoingCancel] = useState(false);

  const runAction = async (setBusy, action, errorMessage, { reloadOnSuccess = false } = {}) => {
    setBusy(true);
    try {
      await action();
      if (reloadOnSuccess) window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || errorMessage);
    } finally {
      setBusy(false);
    }
  };

  const handleManageSubscription = () =>
    runAction(setOpeningPortal, async () => {
      const response = await axios.post(`${API}/subscription/portal`, {}, authHeaders(token));
      window.open(response.data.url, '_blank');
    }, 'Unable to open billing portal');

  const handleAddPayment = async () => {
    setAddingPayment(true);
    try {
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        { origin_url: window.location.origin, plan: user?.selected_plan || 'monthly' },
        authHeaders(token)
      );
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setAddingPayment(false);
    }
  };

  const handleCancelSubscription = (cancelData, onDone) =>
    runAction(setCanceling, async () => {
      const response = await axios.post(`${API}/subscription/cancel`, cancelData, authHeaders(token));
      toast.success(`Subscription canceled. Access until ${new Date(response.data.access_until).toLocaleDateString()}`);
      onDone?.();
    }, 'Failed to cancel subscription', { reloadOnSuccess: true });

  const handlePauseSubscription = (pauseData, onDone) =>
    runAction(setPausing, async () => {
      const response = await axios.post(`${API}/subscription/pause`, pauseData, authHeaders(token));
      toast.success(`Subscription paused. Resumes ${new Date(response.data.resumes_at).toLocaleDateString()}`);
      onDone?.();
    }, 'Failed to pause subscription', { reloadOnSuccess: true });

  const handleResumeSubscription = () =>
    runAction(setResuming, async () => {
      await axios.post(`${API}/subscription/resume`, {}, authHeaders(token));
      toast.success('Subscription resumed!');
    }, 'Failed to resume subscription', { reloadOnSuccess: true });

  const handleUndoCancel = () =>
    runAction(setUndoingCancel, async () => {
      await axios.post(`${API}/subscription/undo-cancel`, {}, authHeaders(token));
      toast.success('Subscription reactivated!');
    }, 'Failed to reactivate subscription', { reloadOnSuccess: true });

  return {
    openingPortal, addingPayment, canceling, pausing, resuming, undoingCancel,
    handleManageSubscription, handleAddPayment, handleCancelSubscription,
    handlePauseSubscription, handleResumeSubscription, handleUndoCancel,
  };
};

export const useReferralActions = (token, referralStats) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  const handleCopyCode = async () => {
    if (referralStats?.share_url) {
      await navigator.clipboard.writeText(referralStats.share_url);
      setCopySuccess(true);
      toast.success('Link copied!');
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) return;
    setSendingInvite(true);
    try {
      await axios.post(
        `${API}/referral/send-invite`,
        { email: inviteEmail },
        authHeaders(token)
      );
      toast.success('Invite sent!');
      setInviteEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  return { copySuccess, inviteEmail, setInviteEmail, sendingInvite, handleCopyCode, handleSendInvite };
};

export const useAccountActions = (token, { twoFaEnabled, setTwoFaEnabled, logout, navigate }) => {
  const [toggling2fa, setToggling2fa] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleToggle2fa = async () => {
    setToggling2fa(true);
    try {
      await axios.post(`${API}/auth/toggle-2fa`, {}, authHeaders(token));
      setTwoFaEnabled(!twoFaEnabled);
      toast.success(twoFaEnabled ? '2FA disabled' : '2FA enabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle 2FA');
    } finally {
      setToggling2fa(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/journal/export-pdf`, {
        ...authHeaders(token),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mindful-renewal-export-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async (deleteConfirmText, deletePassword) => {
    if (deleteConfirmText !== 'DELETE' || !deletePassword) {
      toast.error('Please complete all fields');
      return;
    }
    setDeleting(true);
    try {
      await axios.post(
        `${API}/auth/delete-account`,
        { password: deletePassword, confirmation_text: 'DELETE' },
        authHeaders(token)
      );
      toast.success('Account deleted. We hope your healing continues.');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return { toggling2fa, exporting, deleting, handleToggle2fa, handleExportData, handleDeleteAccount };
};
